import { Skeleton } from "@/components/ui/skeleton";

/**
 * Loading placeholders shaped like the thing that is loading.
 *
 * The prototype's loading state was the text `Loading…`, and its stylesheet
 * carried a `.spinner-wrap` class with no `@keyframes` anywhere in 866 lines — so
 * the spinner it was written for did not spin. More to the point, a centred word
 * gives no indication of what is arriving, and the page jumps when it does.
 *
 * These occupy the layout the content will occupy: same row heights, same column
 * count, same panel sizes. Nothing moves when the data lands, which is the whole
 * purpose.
 *
 * Motion degrades rather than disappearing under `prefers-reduced-motion` —
 * `globals.css` reduces every animation to 0.01ms globally, which leaves these as
 * static blocks that still hold the shape.
 */

/** The page header: eyebrow, title, lead. Every screen opens with one. */
export function SkeletonHeader({ withActions = true }: { withActions?: boolean }) {
  return (
    <div className="border-b border-n3 pb-6" aria-hidden>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Skeleton className="h-6 w-24 rounded-md bg-n2" />
          <Skeleton className="mt-4 h-8 w-56 rounded-md bg-n2" />
          <Skeleton className="mt-3 h-4 w-full max-w-[42ch] rounded bg-n2" />
        </div>
        {withActions ? <Skeleton className="h-10 w-36 rounded-lg bg-n2" /> : null}
      </div>
    </div>
  );
}

/** A filter bar: a search box and a few selects. */
export function SkeletonFilters({ selects = 3 }: { selects?: number }) {
  return (
    <div className="mt-6 flex flex-wrap items-end gap-3" aria-hidden>
      <Skeleton className="h-10 w-full max-w-xs rounded-lg bg-n2" />
      {Array.from({ length: selects }, (_, index) => (
        <Skeleton key={index} className="h-10 w-36 rounded-lg bg-n2" />
      ))}
    </div>
  );
}

/**
 * A table.
 *
 * `rows` defaults to eight because that is roughly what fits above the fold, and
 * a skeleton longer than the viewport advertises work that is not happening.
 */
export function SkeletonTable({ columns = 6, rows = 8 }: { columns?: number; rows?: number }) {
  return (
    <div
      className="panel mt-5 overflow-hidden"
      aria-hidden
    >
      <div className="border-b border-n3 bg-n2/60 px-4 py-3">
        <div className="flex gap-4">
          {Array.from({ length: columns }, (_, index) => (
            <Skeleton key={index} className="h-3 flex-1 rounded bg-n3" />
          ))}
        </div>
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="border-b border-n3 px-4 py-3.5 last:border-0">
          <div className="flex items-center gap-4">
            {Array.from({ length: columns }, (_, column) => (
              <Skeleton
                key={column}
                /* The first column is a name and is always wider — matching that
                   is the difference between a placeholder and a grey rectangle. */
                className={`h-4 rounded bg-n2 ${column === 0 ? "flex-[1.6]" : "flex-1"}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** A `gap-px` hairline tile grid, as the Overview and Reports use. */
export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div
      className="panel mt-8 grid gap-px overflow-hidden bg-n4 p-0 sm:grid-cols-2 lg:grid-cols-4"
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="bg-n0 p-5">
          <Skeleton className="h-3 w-20 rounded bg-n2" />
          <Skeleton className="mt-3 h-7 w-16 rounded bg-n2" />
        </div>
      ))}
    </div>
  );
}

/** A framed panel — a plan, a chart, a coverage board. */
export function SkeletonPanel({ height = "18rem" }: { height?: string }) {
  return (
    <div className="panel mt-6 p-4" aria-hidden>
      <Skeleton className="h-3 w-28 rounded bg-n2" />
      <Skeleton className="mt-3 w-full rounded-md bg-n2" style={{ height }} />
    </div>
  );
}

/**
 * The announcement, once, for the whole page.
 *
 * Every block above is `aria-hidden`: a screen reader has nothing to gain from
 * forty grey rectangles. This is what it hears instead, and `aria-busy` on the
 * region is what tells it the content is coming rather than absent.
 */
export function SkeletonAnnounce({ label }: { label: string }) {
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {label}
    </p>
  );
}
